/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { throttleUntilChanged } from '../../utils/rxjs-utils';
import * as actionType from '../../store/state/actions';
import { ActionManagerInstance } from '../../actions/manager';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import { getResolvedTargetName } from '../../router/routing-manager';
import { isErrorActionType } from '../../case/common-helper';
import { processDataToDispatchErrorAction, processViewResponse, getActionPayload } from '../../store/state/epics/util';
import { publicConstants } from '../../constants';
import { buildContextName, buildModalContext } from '../../utils/context-utils';
import type { Action } from '../../store/state/state-manager';

import executeOpenDataObjectFlowAction from './async-request';
import type { ActionPayload, EndpointData, MapPayload } from './types';

const openDataObjectFlowAction = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: any[] = [];
  const throttleValue = 100;

  return action$.pipe(
    ofType(actionType.OPEN_DATA_OBJECT_ACTION),
    throttleUntilChanged(throttleValue),
    map((action: MapPayload) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionPayload: ActionPayload) => {
      return executeOpenDataObjectFlowAction(actionPayload);
    }),
    mergeMap(async (data: EndpointData) => {
      if (data && data.type === actionType.ERROR) {
        const { payload } = data;
        const errorAction = processDataToDispatchErrorAction({ ...data, ...payload, actionMgrID });
        actionsOut.push(errorAction);
        return actionsOut;
      }
      const { actionInPayload, response, headers = {} } = data;
      const { containerName, actionID, context, actionName } = actionInPayload;
      const target = getResolvedTargetName(containerName, actionInPayload.context);

      let targetContext = buildContextName(containerName, context);
      targetContext = buildModalContext(context, targetContext);
      const commonInfo = {
        target,
        context: targetContext,
        containerItemID: targetContext,
        headers,
        resourceType: publicConstants.RESOURCE_TYPES.DATA,
        resourceStatus: publicConstants.RESOURCE_STATUS.OPEN_FLOW_ACTION,
        key: JSON.stringify(actionInPayload.dataViewParameters),
        actionID,
        actionName
      };

      if (response?.data?.dataInfo) {
        const { dataInfo } = response.data;
        dataInfo.dataContext ||= actionInPayload.lookupDatapage;
        dataInfo.dataContextParameters ||= actionInPayload.dataViewParameters;
      }

      const options = { acName: target, context };
      await processViewResponse(response, actionInPayload, options);
      const payload = getActionPayload(response, commonInfo);
      actionsOut.push(actions.postProcess(actions.addContainerItem, payload));
      return actionsOut;
    }),
    map(() => {
      if (actionsOut.length === 1 && isErrorActionType(actionsOut[0].type)) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
      } else {
        ActionManagerInstance.resolveActionByID(actionMgrID);
      }
      StateManager.dispatch(actionsOut);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export { openDataObjectFlowAction };
